import { useRef, useEffect } from "react";

import { debounce } from "@s-libs/micro-dash";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import getUserProperties from "@/users/getUserProperties";
import contentTypes from "@/view/constants/contentTypes";
import useScrollDepthTracking from "@/view/utils/useScrollDepthTracking";
import VideoDisplay from "./VideoDisplay";
import ArticleDisplay from "./ArticleDisplay";

const propTypes = {
  bannerMode: PropTypes.object.isRequired,
  content: PropTypes.object,
  contentMetadata: PropTypes.array,
  currentLevel: PropTypes.object,
  isVocabularySupportEnabled: PropTypes.bool,
  keyTermDataContentId: PropTypes.string,
  legacyArticleId: PropTypes.number,
  matchedAssignments: PropTypes.array,
  rawText: PropTypes.object,
  readAloudButtonRef: PropTypes.object,
  shouldShowMobileAction: PropTypes.bool,
  toggleVocabularySupport: PropTypes.func,
  user: PropTypes.object,
};

const ContentDisplay = ({
  bannerMode,
  content,
  contentMetadata,
  currentLevel,
  isVocabularySupportEnabled,
  keyTermDataContentId,
  legacyArticleId,
  matchedAssignments,
  rawText,
  readAloudButtonRef,
  shouldShowMobileAction,
  toggleVocabularySupport,
  user,
}) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const userProperties = getUserProperties(user);
  const contentDisplayRef = useRef();
  const isArticle = content.content_type === contentTypes.ARTICLE;

  const { handleArticleReading } = useScrollDepthTracking({
    contentId: levelId,
    userId: userProperties.id,
  });

  const handleScroll = (event) =>
    handleArticleReading({
      scrollTop: event?.target?.scrollTop,
      contentDisplayRef,
    });
  const debouncedHandleScroll = debounce(handleScroll, 75);

  useEffect(() => {
    if (isArticle) {
      const mainElement = document.getElementById("__next");
      mainElement.addEventListener("scroll", debouncedHandleScroll);
      return () =>
        mainElement.removeEventListener("scroll", debouncedHandleScroll);
    }
  }, [handleScroll]);

  switch (content.content_type) {
    case contentTypes.ARTICLE:
      return (
        <ArticleDisplay
          bannerMode={bannerMode}
          content={content}
          contentDisplayRef={contentDisplayRef}
          contentMetadata={contentMetadata}
          currentLevel={currentLevel}
          isVocabularySupportEnabled={isVocabularySupportEnabled}
          keyTermDataContentId={keyTermDataContentId}
          legacyArticleId={legacyArticleId}
          matchedAssignments={matchedAssignments}
          readAloudButtonRef={readAloudButtonRef}
          shouldShowMobileAction={shouldShowMobileAction}
          rawText={rawText}
          toggleVocabularySupport={toggleVocabularySupport}
          userProperties={userProperties}
          user={user}
        />
      );
    case contentTypes.INTERACTIVE_VIDEO:
      return (
        <VideoDisplay
          bannerMode={bannerMode}
          content={content}
          contentMetadata={contentMetadata}
          matchedAssignments={matchedAssignments}
          user={user}
        />
      );
  }
};

ContentDisplay.propTypes = propTypes;

export default ContentDisplay;
